import React, { useState } from "react"
import { Info } from "@phosphor-icons/react"
import { Dialog, DialogBody, DialogFooter } from "@blueprintjs/core"
import CustomButton from "components/Atoms/CustomButton"

/* ── Helper components ────────────────────────────────────────────────── */

const StepCard: React.FC<{
  number: number
  title: string
  color: string
  children: React.ReactNode
}> = ({ number, title, color, children }) => (
  <div
    style={{
      background: "#fff",
      border: `1.5px solid ${color}33`,
      borderRadius: 10,
      marginBottom: 16,
      overflow: "hidden",
    }}
  >
    <div
      style={{
        background: color,
        color: "#fff",
        padding: "8px 16px",
        display: "flex",
        alignItems: "center",
        gap: 10,
        fontWeight: 700,
        fontSize: 13,
      }}
    >
      <span
        style={{
          width: 24,
          height: 24,
          borderRadius: "50%",
          background: "rgba(255,255,255,0.25)",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          fontWeight: 800,
          fontSize: 12,
          flexShrink: 0,
        }}
      >
        {number}
      </span>
      {title}
    </div>
    <div style={{ padding: "12px 16px", lineHeight: 1.75, fontSize: 13 }}>
      {children}
    </div>
  </div>
)

const Callout: React.FC<{
  type: "tip" | "warning" | "info"
  children: React.ReactNode
}> = ({ type, children }) => {
  const styles =
    type === "tip"
      ? { bg: "#f0fdf4", border: "#86efac", icon: "💡", color: "#15803d" }
      : type === "warning"
      ? { bg: "#fffbeb", border: "#fcd34d", icon: "⚠️", color: "#92400e" }
      : { bg: "#eff6ff", border: "#93c5fd", icon: "ℹ️", color: "#1d4ed8" }
  return (
    <div
      style={{
        background: styles.bg,
        border: `1px solid ${styles.border}`,
        borderRadius: 7,
        padding: "8px 12px",
        marginTop: 10,
        fontSize: 12,
        color: styles.color,
        lineHeight: 1.7,
      }}
    >
      <span style={{ marginRight: 6 }}>{styles.icon}</span>
      {children}
    </div>
  )
}

const ScenarioBox: React.FC<{
  title: string
  steps: string[]
  color?: string
}> = ({ title, steps, color = "#6366f1" }) => (
  <div
    style={{
      background: "#fafafa",
      border: `1px solid ${color}44`,
      borderLeft: `3px solid ${color}`,
      borderRadius: "0 8px 8px 0",
      padding: "10px 14px",
      marginTop: 10,
      fontSize: 12,
    }}
  >
    <p style={{ fontWeight: 700, color, marginBottom: 6 }}>📋 {title}</p>
    <ol style={{ paddingLeft: 18, margin: 0, lineHeight: 2 }}>
      {steps.map((s, i) => (
        <li key={i}>{s}</li>
      ))}
    </ol>
  </div>
)

const FAQItem: React.FC<{ q: string; a: string }> = ({ q, a }) => {
  const [open, setOpen] = useState(false)
  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #e2e8f0",
        borderRadius: 8,
        marginBottom: 8,
        overflow: "hidden",
      }}
    >
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          width: "100%",
          textAlign: "left",
          padding: "10px 14px",
          background: "none",
          border: "none",
          cursor: "pointer",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          fontWeight: 600,
          color: "#1d4ed8",
          fontSize: 13,
        }}
      >
        <span>Q: {q}</span>
        <span style={{ fontSize: 16, color: "#94a3b8" }}>
          {open ? "▲" : "▼"}
        </span>
      </button>
      {open && (
        <div
          style={{
            padding: "8px 14px 12px",
            color: "#475569",
            lineHeight: 1.7,
            fontSize: 13,
            borderTop: "1px solid #f1f5f9",
          }}
        >
          A: {a}
        </div>
      )}
    </div>
  )
}

/* ── Main component ───────────────────────────────────────────────────── */

interface PalletizationUserGuideProps {
  isOpen: boolean
  onClose: () => void
}

const PalletizationUserGuide: React.FC<PalletizationUserGuideProps> = ({
  isOpen,
  onClose,
}) => (
  <Dialog
    isOpen={isOpen}
    onClose={onClose}
    title={
      <div className="flex items-center gap-2">
        <Info size={20} /> How to Use — Palletization
      </div>
    }
    style={{ width: "820px", maxWidth: "95vw" }}
    canEscapeKeyClose
    canOutsideClickClose
  >
    <DialogBody className="max-h-[84vh] overflow-y-auto bg-gray-50 text-sm">
      <div style={{ fontFamily: "Inter, sans-serif", color: "#1e293b" }}>
        {/* Intro */}
        <div
          style={{
            background: "linear-gradient(135deg,#1d4ed8,#3b82f6)",
            borderRadius: 10,
            padding: "16px 20px",
            marginBottom: 20,
            color: "#fff",
          }}
        >
          <p style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>
            🏭 Palletization — Quick Guide
          </p>
          <p style={{ opacity: 0.9, lineHeight: 1.6 }}>
            This page lets you assign produce to pallets for a Sales Order.
            Follow the steps in order — the system guides you through each stage
            automatically.
          </p>
        </div>

        {/* ── DATA FLOW OVERVIEW ── */}
        <div
          style={{
            background: "#f8fafc",
            border: "1px solid #cbd5e1",
            borderRadius: 10,
            padding: "16px 20px",
            marginBottom: 20,
          }}
        >
          <p
            style={{
              fontWeight: 700,
              fontSize: 14,
              color: "#0f172a",
              marginBottom: 12,
            }}
          >
            🔗 How Data Flows: Sales Orders & Allocation
          </p>
          <p style={{ color: "#334155", lineHeight: 1.6, marginBottom: 12 }}>
            Palletization is where physical factory stock meets customer demand.
            Before you can palletize, two things must happen:
          </p>
          <ul style={{ paddingLeft: 18, lineHeight: 1.8, color: "#334155" }}>
            <li style={{ marginBottom: 8 }}>
              <b>1. Demand (Sales Order):</b> The Sales Order defines how many
              pallets/boxes are needed and pre-authorizes the <b>MH Codes</b>{" "}
              (farm certificates).{" "}
              <i>
                When you assign an MH Code here, you are picking from the list
                approved on the Sales Order.
              </i>
            </li>
            <li>
              <b>2. Supply (Allocation):</b> Factory receipts (Inwards) generate{" "}
              <b>Gate Passes</b>, which are then assigned to a Holding Area via
              the Allocation page.{" "}
              <i>
                When you select a Gate Pass here, you are picking stock that was
                physically allocated to this line.
              </i>
            </li>
          </ul>
        </div>

        {/* ── STEP 1 ── */}

        <StepCard
          number={1}
          title="Set Production Line & Reference No."
          color="#2563eb"
        >
          <ul style={{ paddingLeft: 18, lineHeight: 2 }}>
            <li>
              Click the <b>Production Line</b> dropdown and select the line
              where this batch will be processed.
            </li>
            <li>
              Enter the <b>Weekly Reference No. (W Ref No)</b> — your internal
              tracking number for the week.
            </li>
            <li>
              Click <b style={{ color: "#16a34a" }}>Confirm</b> to save and
              unlock the pallets below.
            </li>
          </ul>
          <Callout type="info">
            You can change the Production Line at <b>any time</b> until the
            production is <b>Verified</b> by QC. Once verified, the line is
            locked.
          </Callout>

          <ScenarioBox
            title="Scenario — Changing the line mid-way"
            color="#2563eb"
            steps={[
              "Production was started on Line A but the batch was moved to Line B after MH Codes were already assigned.",
              "Select Line B from the dropdown and click Confirm — the line updates without losing any pallet data.",
              "You can do this at any point before QC verifies the production.",
            ]}
          />
          <ScenarioBox
            title="Scenario — Production is already Verified"
            color="#dc2626"
            steps={[
              "The QC supervisor has clicked Verify on this production.",
              "The Production Line dropdown is now disabled and cannot be changed.",
              "If you need to change it, ask QC to Reject the production with a remark first.",
            ]}
          />
        </StepCard>

        {/* ── STEP 2 ── */}
        <StepCard number={2} title="Enter Pallet Numbers" color="#059669">
          <ul style={{ paddingLeft: 18, lineHeight: 2 }}>
            <li>
              Scan or type the <b>physical barcode</b> on the pallet label into
              the <b>Pallet No.</b> field.
            </li>
            <li>
              Each pallet number must be <b>unique</b> — the system shows an
              error if there is a duplicate.
            </li>
            <li>
              <b>Shortcut:</b> Paste a list of pallet numbers (one per line or
              comma-separated) into pallet 1 and they will auto-fill all pallets
              in order.
            </li>
          </ul>

          <ScenarioBox
            title="Scenario — Bulk pasting pallet numbers"
            color="#059669"
            steps={[
              "You have a list of 21 pallet barcodes from the label printer: 7384920156842739, 2749658137406295, ...",
              "Copy all 21 numbers (comma or line-separated).",
              "Click on the Pallet No. field on pallet 1 and paste — all 21 pallets fill in order.",
            ]}
          />
          <ScenarioBox
            title="Scenario — Duplicate pallet number error"
            color="#dc2626"
            steps={[
              "You typed barcode 7384920156842739 on pallet 3, but it was already used on pallet 1.",
              "The system shows a red error: 'Duplicate pallet numbers found'.",
              "Correct pallet 3 to use its proper unique barcode before saving.",
            ]}
          />
        </StepCard>

        {/* ── STEP 3 ── */}
        <StepCard
          number={3}
          title="Assign MH Codes to Each Pallet"
          color="#7c3aed"
        >
          <p style={{ marginBottom: 8 }}>
            Each pallet needs an <b>MH Code</b> (farm certificate) that
            certifies the produce source. This links the pallet to an approved
            supplier. <br />
            <span style={{ color: "#5b21b6", fontSize: 12 }}>
              <b>💡 Where do these come from?</b> The MH Codes available in this
              list are pre-defined by the <b>Sales Order</b>.
            </span>
          </p>
          <ul style={{ paddingLeft: 18, lineHeight: 2 }}>
            <li>
              Click <b>Select MH Code</b> on pallet 1.
            </li>
            <li>
              Choose one or more MH codes — the system distributes boxes across
              pallets automatically.
            </li>
            <li>Repeat for each remaining pallet.</li>
          </ul>
          <Callout type="tip">
            Box counts are set automatically based on the Sales Order format
            (euro vs regular pallets). You don't need to enter them manually.
          </Callout>

          <ScenarioBox
            title="Scenario — Single MH Code for all pallets"
            color="#7c3aed"
            steps={[
              "The SO has 10 pallets × 125 boxes. One MH Code covers the full 1,250 boxes.",
              "Open the MH Code selector on pallet 1 and select that code.",
              "The system fills pallets 1–10 automatically (125 boxes each).",
            ]}
          />
          <ScenarioBox
            title="Scenario — Multiple MH Codes across pallets"
            color="#7c3aed"
            steps={[
              "MH Code A covers 500 boxes (4 pallets), MH Code B covers 750 boxes (6 pallets).",
              "Select MH Code A on pallet 1 — it fills pallets 1–4.",
              "Select MH Code B on pallet 5 — it fills pallets 5–10.",
            ]}
          />
          <ScenarioBox
            title="Scenario — Partial Assignment & Rollover"
            color="#7c3aed"
            steps={[
              "Pallet capacity is 600 kg. You have an MH Code with 1,000 kg capacity.",
              "Select this MH Code on Pallet 1. It takes 600 kg to fill Pallet 1.",
              "The remaining 400 kg automatically rolls over to Pallet 2.",
              "Pallet 2 now needs 200 kg more. Select another MH Code on Pallet 2 to top it up to 600 kg.",
            ]}
          />
          <ScenarioBox
            title="Scenario — Replacing an MH Code (Mistake Fix)"
            color="#7c3aed"
            steps={[
              "You assigned MH Code A to Pallet 1, but realize you need MH Code B.",
              "Simply click 'Select MH Code' on Pallet 1 again.",
              "Uncheck MH Code A, and check MH Code B.",
              "Click Confirm. The system will cleanly remove MH Code A and apply MH Code B.",
            ]}
          />
        </StepCard>

        {/* ── STEP 4 ── */}
        <StepCard
          number={4}
          title="Assign Gate Passes to Each Pallet"
          color="#0891b2"
        >
          <p style={{ marginBottom: 8 }}>
            Gate Passes are the physical delivery records of produce that
            arrived at the factory. Each pallet must be linked to the gate
            passes that supplied its produce. <br />
            <span style={{ color: "#164e63", fontSize: 12 }}>
              <b>💡 Where do these come from?</b> The Gate Passes available in
              this list are those that were assigned to this production during
              the <b>Allocation</b> process.
            </span>
          </p>
          <ul style={{ paddingLeft: 18, lineHeight: 2 }}>
            <li>
              Click <b>Select Gate Pass</b> on any pallet. A list of available
              factory receipts will open.
            </li>
            <li>
              <b>How to select:</b> Check the box next to the gate passes you
              want. The system fills the current pallet first, then
              automatically carries any remaining weight over to the next
              pallets.
            </li>
            <li>
              <b>Additional Qty:</b> If a gate pass was previously saved but
              more stock arrived (or wasn't fully entered), you will see an
              "Additional Weight" input field below it. Enter the newly arrived
              kg there to add it to the pool.
            </li>
            <li>
              If a gate pass has more weight than all pallets can hold combined,
              you will be asked if the remainder should be marked as{" "}
              <b style={{ color: "#dc2626" }}>rejection</b>.
            </li>
          </ul>
          <Callout type="warning">
            A gate pass assigned to one pallet will appear <b>greyed out</b> in
            all other pallets. This is correct — produce cannot be counted
            twice.
          </Callout>
          <Callout type="tip">
            For <b>MIX variety</b> orders, the system divides each pallet
            equally between varieties. Select gate passes for each variety and
            let the system handle the split.
          </Callout>

          <ScenarioBox
            title="Scenario — Partial Assignment & Rollover"
            color="#0891b2"
            steps={[
              "Pallet capacity is 600 kg. Gate Pass 2100272 has 1,000 kg.",
              "Select GP-2100272 on Pallet 1. Pallet 1 takes 600 kg and becomes full.",
              "The remaining 400 kg automatically rolls over to Pallet 2.",
              "Pallet 2 still needs 200 kg to be full. Select a second Gate Pass on Pallet 2 to fill the remaining 200 kg.",
              "Pallet 2 will now show both gate passes correctly assigned.",
            ]}
          />
          <ScenarioBox
            title="Scenario — Single variety, gate pass spans multiple pallets"
            color="#0891b2"
            steps={[
              "Gate Pass 1904213 has 1,200 kg. Each pallet holds 600 kg.",
              "Open the gate pass selector on pallet 1 and tick GP-1904213.",
              "System fills pallet 1 with 600 kg, then carries the remaining 600 kg to pallet 2 automatically.",
            ]}
          />
          <ScenarioBox
            title="Scenario — MIX variety (2 varieties per pallet)"
            color="#0891b2"
            steps={[
              "SO requires Kesar + Alphonso on each pallet (300 kg each = 600 kg total).",
              "Select GP-Kesar in the gate pass dialog — system puts 300 kg of Kesar in each pallet.",
              "Select GP-Alphonso next — system puts 300 kg of Alphonso in each pallet.",
              "Each pallet now shows both gate passes and a total of 600 kg.",
            ]}
          />
          <ScenarioBox
            title="Scenario — Gate pass has leftover weight"
            color="#0891b2"
            steps={[
              "GP-2100272 has 650 kg but all 10 pallets are already full after 600 kg.",
              "After confirming, a 'Remaining Weight' dialog appears showing 50 kg unassigned.",
              "You can enter a rejection amount (e.g. 50 kg) or click 'Keep Available' to leave it for another production.",
            ]}
          />
          <ScenarioBox
            title="Scenario — Adding more weight to an already-saved gate pass"
            color="#0891b2"
            steps={[
              "Production was saved. GP-2100185 has received additional stock and now has more available weight.",
              "Reopen the gate pass selector — GP-2100185 appears with an 'Additional Weight' input below it.",
              "Enter the new kg to add. The system distributes it across pallets that still have capacity.",
            ]}
          />
        </StepCard>

        {/* ── STEP 5 ── */}
        <StepCard number={5} title="Save & Send for Approval" color="#d97706">
          <ul style={{ paddingLeft: 18, lineHeight: 2 }}>
            <li>
              Click <b style={{ color: "#16a34a" }}>Save</b> at the bottom to
              save your progress at any time — you can come back later.
            </li>
            <li>
              Once all pallets are fully filled with correct box counts, the{" "}
              <b style={{ color: "#2563eb" }}>Send for Approval</b> button
              becomes active.
            </li>
            <li>
              Clicking Send for Approval submits the production to a QC
              supervisor for review.
            </li>
          </ul>
          <Callout type="tip">
            Save frequently! Changes live in your browser until you click Save.
          </Callout>
          <Callout type="warning">
            The Send for Approval button stays greyed out if any pallet has a
            wrong box count or missing MH Code / Gate Pass. Check for red
            warning banners on individual pallets.
          </Callout>

          <ScenarioBox
            title="Scenario — Coming back to a partially filled production"
            color="#d97706"
            steps={[
              "You filled 15 out of 21 pallets and clicked Save, then left for lunch.",
              "Reopen the production — the 15 filled pallets load exactly as you left them.",
              "Continue filling pallets 16–21, save again, then send for approval.",
            ]}
          />
          <ScenarioBox
            title="Scenario — Send for Approval is greyed out"
            color="#d97706"
            steps={[
              "Pallet 7 has an MH Code but no Gate Pass assigned.",
              "Pallet 12 has 124 boxes instead of the required 125.",
              "Fix both pallets, save, and the Send for Approval button will enable.",
            ]}
          />
        </StepCard>

        {/* ── PALLET ACTIONS ── */}
        <div
          style={{
            background: "#fafafa",
            border: "1px solid #e5e7eb",
            borderRadius: 10,
            padding: "14px 18px",
            marginBottom: 16,
          }}
        >
          <p
            style={{
              fontWeight: 700,
              fontSize: 14,
              color: "#374151",
              marginBottom: 8,
            }}
          >
            🛠️ Pallet Actions (Options Menu)
          </p>
          <p style={{ marginBottom: 10 }}>
            Click the <b>⋮ (three dots)</b> menu on the top right of any pallet
            card to access extra actions.
          </p>

          <ul style={{ paddingLeft: 18, lineHeight: 2, color: "#4b5563" }}>
            <li>
              <b>Remark:</b> Add notes or select predefined tags (e.g., "Damaged
              box", "Seal broken") to explain any issues with this pallet.
            </li>
            <li>
              <b>Pallet Type:</b> If the Sales Order allows Euro pallets, you
              can manually toggle this pallet between "Regular" and "Euro".{" "}
              <i>
                (Note: A production can only have one Euro pallet, usually the
                last one).
              </i>
            </li>
            <li>
              <b>Rejection:</b> Use this to adjust the packed boxes if some
              produce from the assigned gate passes was rejected during packing.
              The system calculates the rejected weight automatically.
            </li>
            <li>
              <b>Reverse Stock:</b> If you assigned the wrong MH Code or Gate
              Pass, use this to return it to the available pool.{" "}
              <i>(Disabled after sending for approval).</i>
            </li>
          </ul>

          <ScenarioBox
            title="Scenario — Rejecting damaged boxes"
            color="#4b5563"
            steps={[
              "Pallet 5 was supposed to pack 125 boxes, but 5 boxes were damaged during packing.",
              "Click ⋮ → Rejection. In the dialog, lower the 'Packed Boxes' from 125 to 120.",
              "Click Confirm Rejection. The system logs 5 boxes as rejected weight and frees up space in the pallet to assign more stock.",
            ]}
          />
          <ScenarioBox
            title="Scenario — Fixing a mistake (Reverse Stock)"
            color="#4b5563"
            steps={[
              "You accidentally assigned MH Code B to Pallet 1, but it should have been MH Code A.",
              "Click ⋮ → Reverse Stock on Pallet 1.",
              "Click 'Reverse Stock MH Codes'. The assignment is cleared from the pallet, and MH Code B goes back into the available pool.",
            ]}
          />
        </div>

        {/* ── LOT CODE EDITING ── */}
        <div
          style={{
            background: "#fafafa",
            border: "1px solid #e5e7eb",
            borderRadius: 10,
            padding: "14px 18px",
            marginBottom: 16,
          }}
        >
          <p
            style={{
              fontWeight: 700,
              fontSize: 14,
              color: "#374151",
              marginBottom: 8,
            }}
          >
            ✏️ Lot Code & Production Date Editing
          </p>
          <p style={{ marginBottom: 10 }}>
            After a pallet is saved, the system automatically generates a Lot
            Code for each Gate Pass assigned to it. You can edit this Lot Code
            or adjust the Production Created Date directly on the pallet card.
          </p>

          <ul style={{ paddingLeft: 18, lineHeight: 2, color: "#4b5563" }}>
            <li>
              <b>How to open:</b> Look at the top left of the pallet card for{" "}
              <b>Date & LOT code:</b>.
            </li>
            <li>
              <b>Click the text</b> (e.g.,{" "}
              <code
                style={{
                  background: "#e5e7eb",
                  padding: "2px 4px",
                  borderRadius: 4,
                }}
              >
                1904213 - 2026-07-03 - 12345678
              </code>
              ) to open the edit menu.
            </li>
            <li>
              <b>Edit fields:</b> You can type a new custom Lot Code, OR change
              the Production Created Date.
            </li>
            <li>
              <b>Save:</b> Click the <b>Save</b> button. The updated Lot Code or
              Date will refresh and apply immediately.
            </li>
          </ul>

          <ScenarioBox
            title="Scenario — Updating an incorrect Production Date"
            color="#4b5563"
            steps={[
              "The default date for a gate pass's lot code is today, but the produce was actually packed yesterday.",
              "Click on the Lot Code text on the pallet.",
              "In the popover, change the 'Production Created Date' to yesterday's date, then click Save.",
            ]}
          />
        </div>

        {/* ── QC section ── */}
        <div
          style={{
            background: "#f0fdf4",
            border: "1px solid #bbf7d0",
            borderRadius: 10,
            padding: "14px 18px",
            marginBottom: 16,
          }}
        >
          <p
            style={{
              fontWeight: 700,
              fontSize: 13,
              color: "#15803d",
              marginBottom: 8,
            }}
          >
            🔍 For QC Supervisors — Verify or Reject
          </p>
          <ul style={{ paddingLeft: 18, lineHeight: 2, color: "#166534" }}>
            <li>
              Open the production and check that all pallets are correctly
              filled.
            </li>
            <li>
              Click the <b>✓ Verified</b> or <b>✗ Rejected</b> badge at the top
              right to see the full remark history.
            </li>
            <li>
              Click <b style={{ color: "#16a34a" }}>Verify</b> to approve, or{" "}
              <b style={{ color: "#dc2626" }}>Reject</b> to send back — a remark
              is required for both actions.
            </li>
            <li>
              Once <b>Verified</b>, the Production Line is locked and no further
              line changes can be made.
            </li>
          </ul>

          <ScenarioBox
            title="Scenario — Rejecting and re-submitting"
            color="#dc2626"
            steps={[
              "QC rejects the production: 'Pallet 3 gate pass weight does not match'.",
              "The production supervisor sees the Rejected badge and clicks it to read the remark.",
              "They fix pallet 3, save, and click Send for Approval again.",
              "QC reviews once more and this time clicks Verify.",
            ]}
          />
          <ScenarioBox
            title="Scenario — Verifying and locking the production"
            color="#15803d"
            steps={[
              "QC confirms all 21 pallets are correct.",
              "QC clicks Verify and enters a remark: 'All pallets checked, weights match'.",
              "The production now shows a green ✓ Verified badge.",
              "The Production Line dropdown is now disabled — no further changes to the line are allowed.",
            ]}
          />
        </div>

        {/* ── PRINTS & LABELS ── */}
        <div
          style={{
            background: "#fff",
            border: "1.5px solid #f59e0b33",
            borderRadius: 10,
            marginBottom: 16,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              background: "#d97706",
              color: "#fff",
              padding: "8px 16px",
              display: "flex",
              alignItems: "center",
              gap: 10,
              fontWeight: 700,
              fontSize: 13,
            }}
          >
            <span
              style={{
                width: 24,
                height: 24,
                borderRadius: "50%",
                background: "rgba(255,255,255,0.25)",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 800,
                fontSize: 12,
                flexShrink: 0,
              }}
            >
              🖨
            </span>
            Printing Labels
          </div>
          <div style={{ padding: "12px 16px", lineHeight: 1.75, fontSize: 13 }}>
            <p style={{ marginBottom: 10 }}>
              Every pallet has a <b>Print Labels</b> button (🖨) on its row.
              Three label types are available — each serves a different purpose
              on the factory floor and at customs.
            </p>

            {/* Label types table */}
            <div
              style={{
                background: "#fafafa",
                border: "1px solid #e2e8f0",
                borderRadius: 8,
                overflow: "hidden",
                marginBottom: 14,
              }}
            >
              {[
                {
                  icon: "📦",
                  name: "Box End Label",
                  size: "100 × 75 mm (S)",
                  desc: "Attached to the end of each export carton. Shows product, variety, lot code, farm reg. no., net weight, GGN, and country of origin.",
                },
                {
                  icon: "🏷️",
                  name: "SSCC Label",
                  size: "100 × 150 mm (L)",
                  desc: "GS1-128 pallet label with a scannable barcode. Contains the full SSCC number, pallet details, box count, weight, and lot codes. Required for container scanning.",
                },
                {
                  icon: "🔢",
                  name: "Pallet No. Label",
                  size: "100 × 75 mm (S)",
                  desc: "A large, bold pallet identifier used internally. Shows the last 6 digits of the pallet code with MH Code rows and total box count.",
                },
              ].map((row, i) => (
                <div
                  key={i}
                  style={{
                    display: "flex",
                    gap: 12,
                    padding: "10px 14px",
                    borderBottom: i < 2 ? "1px solid #e2e8f0" : undefined,
                    alignItems: "flex-start",
                  }}
                >
                  <span style={{ fontSize: 20, flexShrink: 0 }}>
                    {row.icon}
                  </span>
                  <div>
                    <p style={{ fontWeight: 700, marginBottom: 2 }}>
                      {row.name}{" "}
                      <span
                        style={{
                          fontWeight: 400,
                          color: "#64748b",
                          fontSize: 11,
                        }}
                      >
                        ({row.size})
                      </span>
                    </p>
                    <p style={{ color: "#475569", margin: 0 }}>{row.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* How to print */}
            <p style={{ fontWeight: 700, marginBottom: 6 }}>
              📋 How to Print a Label
            </p>
            <ol style={{ paddingLeft: 18, margin: 0, lineHeight: 2 }}>
              <li>
                Click the <b>🖨 Print Labels</b> button on the pallet row.
              </li>
              <li>
                Choose the label type from the dialog: <b>Box End</b>,{" "}
                <b>SSCC</b>, or <b>Pallet No.</b>
              </li>
              <li>
                Use the <b>Print Size</b> dropdown to change the label
                dimensions if needed (S / M / L).
              </li>
              <li>
                Click <b>Rotate Layout</b> to flip the label orientation
                (landscape/portrait).
              </li>
              <li>
                Click <b>Print / Download</b> — a print preview window opens.
                Select your label printer and confirm.
              </li>
            </ol>

            <Callout type="tip">
              Make sure your label printer page size matches the label size
              selected (e.g. <b>100 × 150 mm</b> for SSCC). Set the page margins
              to <b>0</b> in the print dialog for best results.
            </Callout>

            {/* Driver section */}
            <div
              style={{
                background: "#eff6ff",
                border: "1px solid #93c5fd",
                borderRadius: 8,
                padding: "12px 14px",
                marginTop: 14,
              }}
            >
              <p
                style={{
                  fontWeight: 700,
                  color: "#1d4ed8",
                  marginBottom: 8,
                  fontSize: 13,
                }}
              >
                🖨️ Argox Thermal Printer — Driver Installation
              </p>
              <p style={{ color: "#334155", marginBottom: 8, lineHeight: 1.7 }}>
                Labels are designed for <b>Argox thermal label printers</b>. If
                your computer does not detect the printer, you need to install
                the Argox driver first.
              </p>
              <ol
                style={{
                  paddingLeft: 18,
                  margin: 0,
                  lineHeight: 2,
                  color: "#1e3a5f",
                }}
              >
                <li>
                  Visit the official Argox driver download page:{" "}
                  <a
                    href="https://www.argox.com/download/drivers/"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      color: "#1d4ed8",
                      fontWeight: 600,
                      textDecoration: "underline",
                    }}
                  >
                    https://www.argox.com/download/drivers/
                  </a>
                </li>
                <li>
                  Find your printer model (e.g. <b>OS-214 Plus</b>,{" "}
                  <b>CP-2240</b>), download the Windows driver ZIP, and extract
                  it.
                </li>
                <li>
                  Run the installer as <b>Administrator</b>, follow the wizard,
                  and connect the printer via USB or LAN when prompted.
                </li>
                <li>
                  In Windows, go to <b>Printers &amp; Scanners</b>, confirm the
                  Argox printer appears, then set it as the{" "}
                  <b>default printer</b>.
                </li>
                <li>
                  In the browser print dialog, select <b>your Argox printer</b>{" "}
                  (not "Save as PDF"), set paper size to match the label, and
                  set margins to <b>None / 0</b>.
                </li>
              </ol>
              <Callout type="warning">
                Do <b>not</b> use the "Microsoft Print to PDF" or any virtual
                printer — it will produce a PDF instead of printing to the label
                roll. Always select the physical Argox printer.
              </Callout>
            </div>
          </div>
        </div>

        {/* ── FAQ ── */}

        <div style={{ marginTop: 4 }}>
          <p
            style={{
              fontWeight: 700,
              fontSize: 14,
              color: "#1e293b",
              marginBottom: 10,
            }}
          >
            ❓ Common Questions
          </p>
          {[
            {
              q: "The 'Send for Approval' button is greyed out — why?",
              a: "All pallets must be fully filled with MH Codes, Gate Passes, and the correct box count. Scroll down and look for red warning banners on individual pallets — they will tell you exactly what is missing.",
            },
            {
              q: "I assigned the wrong gate pass to a pallet — how do I fix it?",
              a: "Use 'Restock Gate Passes' at the bottom of the page to return all gate passes to the available pool, then re-assign them. Note: this clears gate passes from every pallet at once.",
            },
            {
              q: "A gate pass is greyed out in the selector — why?",
              a: "That gate pass is already assigned to another pallet in this production. Each gate pass can only appear once per production run to prevent double-counting.",
            },
            {
              q: "Can I change the Production Line after filling pallets?",
              a: "Yes — you can change the Production Line at any time right up until the QC supervisor clicks Verify. After verification the line is permanently locked for that production.",
            },
            {
              q: "Why does the Gross weight look slightly different from what I expect?",
              a: "The Gross weight is the exact sum of kilograms from all assigned gate passes. If it looks different from the Sales Order weight, check whether any gate pass has a rejection recorded against it, or whether a partial assignment was made.",
            },
            {
              q: "What happens if a gate pass has leftover weight after filling all pallets?",
              a: "The system shows a 'Remaining Weight' dialog. You can enter a rejection amount for the leftover kg, or click 'Keep Available' to leave it in the pool for use in another production.",
            },
            {
              q: "How do I fill pallet numbers quickly without typing each one?",
              a: "Copy all your pallet barcodes (comma or line-separated) from the label software, then paste them directly into the Pallet No. field on pallet 1. The system will auto-fill all pallets in order.",
            },
          ].map((item, i) => (
            <FAQItem key={i} q={item.q} a={item.a} />
          ))}
        </div>
      </div>
    </DialogBody>
    <DialogFooter>
      <CustomButton intent="primary" onClick={onClose} round>
        Got it!
      </CustomButton>
    </DialogFooter>
  </Dialog>
)

export default PalletizationUserGuide
